import mergeRefs from 'merge-refs';
import {
  useState,
  useRef,
  type ComponentRef,
  type RefObject,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { afterFrames } from '@/lib/animate';
import { classList } from '@/utils/cn';

import { type RenameFieldRef } from './useRenameField';

interface RenameFieldProps {
  value: string;
  onRename(value: string): void;
  onFocusRenameField?(): void;
  onBlurRenameField?(): void;
  className?: string;
  isLoading?: boolean;
  maxWidth?: number;
  disabled?: boolean;
  editOnClick?: boolean;
  autoResize?: boolean;
}

export const RenameField = forwardRef<RenameFieldRef, RenameFieldProps>(
  (
    {
      value,
      onRename,
      onFocusRenameField,
      onBlurRenameField,
      className,
      isLoading = false,
      maxWidth,
      disabled = false,
      editOnClick = false,
      autoResize = false,
    }: RenameFieldProps,
    ref
  ) => {
    const renameButtonRef = useRef<ComponentRef<'button'>>(null);
    const renameInputFieldRef = useRef<ComponentRef<'input'>>(null);
    const [isShowRenameInputField, setShowRenameInputField] = useState(false);
    const [width, setWidth] = useState(0);
    const cancelledRef = useRef(false);

    useImperativeHandle(ref, () => ({
      renameButtonRef: renameButtonRef,
      renameInputFieldRef: renameInputFieldRef,
    }));

    const showRenameInputField = useCallback(() => {
      if (disabled) return;
      cancelledRef.current = false;
      if (renameButtonRef.current) {
        const { width } = renameButtonRef.current.getBoundingClientRect();

        if (width > 0) setWidth(Math.min(width, maxWidth ?? width));
      }

      setShowRenameInputField(true);

      afterFrames(2, () => {
        if (!renameInputFieldRef.current) return;

        const element = renameInputFieldRef.current;

        element.focus({ preventScroll: true });

        element.select();
      });
    }, [maxWidth, disabled]);

    const hideRenameInputField = () => {
      if (!renameInputFieldRef.current) return;

      const element = renameInputFieldRef.current;

      cancelledRef.current = true;
      setShowRenameInputField(false);

      element.blur();
    };

    const rename = useCallback(() => {
      if (!renameInputFieldRef.current) return;

      const text = renameInputFieldRef.current.value.trim();

      if (!cancelledRef.current && text && text !== value.trim()) {
        onRename(text);
      }

      setShowRenameInputField(false);

      if (onBlurRenameField) onBlurRenameField();
    }, [onRename, onBlurRenameField, value]);

    const escKeyBlursInputRef = useHotkeys(
      'esc',
      hideRenameInputField,
      {
        enabled: isShowRenameInputField,
        preventDefault: true,
        enableOnContentEditable: true,
        enableOnFormTags: true,
      },
      [isShowRenameInputField]
    );

    const enterKeyConfirmsRenameRef = useHotkeys(
      'enter',
      rename,
      {
        enabled: isShowRenameInputField,
        preventDefault: true,
        enableOnContentEditable: true,
        enableOnFormTags: true,
      },
      [isShowRenameInputField]
    );

    if (!isShowRenameInputField)
      return (
        <>
          <button
            ref={renameButtonRef}
            type="button"
            onClick={editOnClick ? showRenameInputField : undefined}
            onDoubleClick={showRenameInputField}
            tabIndex={editOnClick ? 0 : -1}
            className={classList(
              'truncate text-left whitespace-nowrap',
              autoResize ? 'w-fit max-w-full' : 'w-full',
              editOnClick
                ? 'cursor-text rounded-lg transition-[background-color,box-shadow] duration-150 ease-out hover:bg-grayA-2 hover:shadow-[0_0_0_4px_var(--gray-a2)]'
                : 'cursor-default',
              isLoading && [
                'bg-[linear-gradient(to_right,var(--gray-11)_0%,var(--gray-11)_67.5%,var(--gray-12)_72.5%,var(--gray-12)_87.5%,var(--gray-11)_92.5%,var(--gray-11)_100%)]',
                'bg-[length:200%_100%]',
                'bg-clip-text text-transparent',
                '[background-position:0%_0]',
                'animate-shimmer-rename-field',
              ],
              className
            )}
            style={{ maxWidth }}
            aria-label={
              editOnClick
                ? `Rename ${value}`
                : `Double-click to rename ${value}`
            }
            aria-busy={isLoading}
            aria-live={isLoading ? 'polite' : undefined}
          >
            {value}
          </button>
        </>
      );

    return (
      <div className={autoResize ? 'min-w-0 max-w-full' : undefined}>
        <input
          ref={
            mergeRefs(
              renameInputFieldRef,
              escKeyBlursInputRef,
              enterKeyConfirmsRenameRef
            ) as RefObject<HTMLInputElement | null>
          }
          style={
            autoResize
              ? { maxWidth }
              : { width: width > 0 ? `${width}px` : 'auto', maxWidth }
          }
          className={classList(
            'bg-transparent p-0 m-0 focus:outline-none',
            autoResize && '[field-sizing:content] min-w-[1ch] max-w-full',
            className
          )}
          defaultValue={value}
          type="text"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          onBlur={rename}
          onFocus={onFocusRenameField}
          aria-label={`Rename from ${value}`}
          aria-details="rename-instructions"
          placeholder={value}
        />

        {isShowRenameInputField && (
          <span id="rename-instructions" className="sr-only">
            Press Enter to save or Escape to cancel
          </span>
        )}
      </div>
    );
  }
);
